import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody, Container, CardHeader } from "reactstrap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { SkeletonTheme } from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import { APIClient } from "../../helpers/api_helper";
import { translateApiError } from "../../helpers/translateApiError";
import "./DashboardStatistics.css";

const api = new APIClient();

// Simple Stat Card Component
const StatCard = ({ title, value }) => {
  return (
    <div className="dashboard-stat-card">
      <div className="dashboard-stat-title">{title}</div>
      <h2 className="dashboard-stat-value">
        {typeof value === "number" ? value.toLocaleString() : value}
      </h2>
    </div>
  );
};

// Loading Component
const LoadingScreen = () => {
  const { t } = useTranslation();
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center">
        <div
          className="spinner-border text-primary mb-3"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">{t("common.loading")}</span>
        </div>
        <h4 className="mb-2">{t("dashboard.admin.loading")}</h4>
        <p className="text-muted">{t("dashboard.admin.loadingHint")}</p>
      </div>
    </div>
  );
};

// Error Component
const ErrorScreen = ({ error, handleRefresh }) => {
  const { t } = useTranslation();
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center">
        <h3 className="mb-3">{t("dashboard.admin.errorTitle")}</h3>
        <p className="text-muted mb-4">{error}</p>
        <button onClick={handleRefresh} className="btn btn-primary">
          {t("dashboard.admin.tryAgain")}
        </button>
      </div>
    </div>
  );
};

// Format the chart label from the period start date in the current language.
// The backend label is English ("Jan 15", "Jan 2025"), so it is only a fallback.
const formatPeriodLabel = (item, language) => {
  const date = item.date ? new Date(item.date) : null;
  if (!date || isNaN(date.getTime())) return item.name;
  const options =
    item.granularity === "monthly"
      ? { year: "numeric", month: "short", timeZone: "UTC" }
      : { month: "short", day: "numeric", timeZone: "UTC" };
  return date.toLocaleDateString(language, options);
};

// Transform API trend data to chart format
const transformTrendData = (trend, granularity) => {
  return trend.map((item) => ({
    name: item.period,
    granularity,
    date: item.date,
    totalTenants: item.totalTenants,
    totalUsers: item.totalUsers,
    activeTenants: item.activeTenants,
    activeUsers: item.activeUsers,
  }));
};

// Main Dashboard Component
const DashboardStatistics = () => {
  const { t, i18n } = useTranslation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    activeTenants: 0,
    activeUsers: 0,
  });
  const [chartData, setChartData] = useState([]);

  // Fetch analytics data from API whenever period changes
  useEffect(() => {
    const fetchData = async () => {
      // Only show full loading screen on initial load
      if (initialLoading) {
        setInitialLoading(true);
      } else {
        setFetchingData(true);
      }

      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/dashboard/analytics?period=${period}`
        );

        // Check if response has the expected structure
        if (response?.data?.current && response?.data?.trend) {
          setStats(response.data.current);
          setChartData(transformTrendData(response.data.trend, period));
          setError(null);
        } else {
          throw new Error(t("dashboard.admin.invalidResponse"));
        }
      } catch (err) {
        console.error("Error fetching dashboard analytics:", err);
        setError(
          translateApiError(
            err?.response?.data?.message ? err.response.data : err,
            t("dashboard.admin.fetchError")
          )
        );
      } finally {
        setInitialLoading(false);
        setFetchingData(false);
      }
    };

    fetchData();
  }, [period]); // Refetch when period changes

  const handleRefresh = () => {
    window.location.reload();
  };

  if (initialLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} handleRefresh={handleRefresh} />;
  }

  return (
    <SkeletonTheme baseColor="#F0F4F6" highlightColor="#e9ebec">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card className="dashboard-content">
              <CardHeader className="border-0">
                <div>
                  <h2 className="mb-3">{t("dashboard.pageTitle")}</h2>
                  <br />
                  <hr />
                  <div className="dashboard-period-btn-group">
                    <button
                      type="button"
                      className={`dashboard-period-btn ${
                        period === "daily" ? "active" : ""
                      }`}
                      onClick={() => setPeriod("daily")}
                    >
                      {t("dashboard.admin.periods.daily")}
                    </button>
                    <button
                      type="button"
                      className={`dashboard-period-btn ${
                        period === "weekly" ? "active" : ""
                      }`}
                      onClick={() => setPeriod("weekly")}
                    >
                      {t("dashboard.admin.periods.weekly")}
                    </button>
                    <button
                      type="button"
                      className={`dashboard-period-btn ${
                        period === "monthly" ? "active" : ""
                      }`}
                      onClick={() => setPeriod("monthly")}
                    >
                      {t("dashboard.admin.periods.monthly")}
                    </button>
                  </div>
                </div>
              </CardHeader>

              <div className="card-body pt-0">
                {/* Stats Cards */}
                <Row
                  className="mb-4"
                  style={{
                    position: "relative",
                    opacity: fetchingData ? 0.6 : 1,
                    transition: "opacity 0.3s",
                  }}
                >
                  {fetchingData && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                      }}
                    ></div>
                  )}
                  <Col xl={3} md={6}>
                    <Card className="metric-card">
                      <CardBody>
                        <div className="dashboard-stat-card">
                          <div className="dashboard-stat-title">
                            {t("dashboard.admin.stats.totalTenants")}
                          </div>
                          <h2 className="dashboard-stat-value">
                            {stats.totalTenants.toLocaleString()}
                          </h2>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} md={6}>
                    <Card className="metric-card">
                      <CardBody>
                        <div className="dashboard-stat-card">
                          <div className="dashboard-stat-title">
                            {t("dashboard.admin.stats.totalUsers")}
                          </div>
                          <h2 className="dashboard-stat-value">
                            {stats.totalUsers.toLocaleString()}
                          </h2>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} md={6}>
                    <Card className="metric-card">
                      <CardBody>
                        <div className="dashboard-stat-card">
                          <div className="dashboard-stat-title">
                            {t("dashboard.admin.stats.activeTenants")}
                          </div>
                          <h2 className="dashboard-stat-value">
                            {stats.activeTenants.toLocaleString()}
                          </h2>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col xl={3} md={6}>
                    <Card className="metric-card">
                      <CardBody>
                        <div className="dashboard-stat-card">
                          <div className="dashboard-stat-title">
                            {t("dashboard.admin.stats.activeUsers")}
                          </div>
                          <h2 className="dashboard-stat-value">
                            {stats.activeUsers.toLocaleString()}
                          </h2>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Chart */}
                <div
                  className="dashboard-chart-card"
                  style={{ position: "relative" }}
                >
                  <div className="dashboard-chart-header">
                    <h5 className="dashboard-header-title">
                      {t("dashboard.admin.trendsTitle")}
                    </h5>
                    <p className="dashboard-header-subtitle">
                      {period === "daily" &&
                        t("dashboard.admin.periodRange.daily")}
                      {period === "weekly" &&
                        t("dashboard.admin.periodRange.weekly")}
                      {period === "monthly" &&
                        t("dashboard.admin.periodRange.monthly")}
                    </p>
                  </div>
                  <div
                    className="dashboard-chart-body"
                    style={{
                      opacity: fetchingData ? 0.6 : 1,
                      transition: "opacity 0.3s",
                    }}
                  >
                    {fetchingData && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 10,
                        }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">
                            {t("common.loading")}
                          </span>
                        </div>
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height={400}>
                      <AreaChart
                        data={chartData.map((item) => ({
                          ...item,
                          name: formatPeriodLabel(item, i18n.language),
                        }))}
                      >
                        <defs>
                          <linearGradient
                            id="totalTenantsGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#2B7FFF"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#2B7FFF"
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                          <linearGradient
                            id="totalUsersGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#B692F6"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#B692F6"
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                          <linearGradient
                            id="activeTenantsGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#53389E"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#53389E"
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                          <linearGradient
                            id="activeUsersGradient"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#12B76A"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#12B76A"
                              stopOpacity={0.05}
                            />
                          </linearGradient>
                        </defs>
                        {/* <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" /> */}
                        <XAxis dataKey="name" stroke="#6c757d" fontSize={12} />
                        <YAxis stroke="#6c757d" fontSize={12} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#fff",
                            border: "1px solid #dee2e6",
                            borderRadius: "0.5rem",
                            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                          }}
                        />
                        <Legend
                          verticalAlign="top"
                          align="right"
                          wrapperStyle={{ paddingBottom: "20px" }}
                        />
                        <Area
                          type="monotone"
                          dataKey="totalTenants"
                          stroke="#2B7FFF"
                          fill="url(#totalTenantsGradient)"
                          strokeWidth={2}
                          name={t("dashboard.admin.stats.totalTenants")}
                        />
                        <Area
                          type="monotone"
                          dataKey="totalUsers"
                          stroke="#B692F6"
                          fill="url(#totalUsersGradient)"
                          strokeWidth={2}
                          name={t("dashboard.admin.stats.totalUsers")}
                        />
                        <Area
                          type="monotone"
                          dataKey="activeTenants"
                          stroke="#53389E"
                          fill="url(#activeTenantsGradient)"
                          strokeWidth={2}
                          name={t("dashboard.admin.stats.activeTenants")}
                        />
                        <Area
                          type="monotone"
                          dataKey="activeUsers"
                          stroke="#12B76A"
                          fill="url(#activeUsersGradient)"
                          strokeWidth={2}
                          name={t("dashboard.admin.stats.activeUsers")}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </SkeletonTheme>
  );
};

export default DashboardStatistics;
